/**
 * ZoomComboControl Component
 *
 * Combo input + dropdown for zoom control.
 * Supports typing "fit" or numbers (e.g., "137") and selecting from presets.
 *
 * @module ZoomComboControl
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from '@/lib/utils';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import { ZOOM_LEVELS } from '@/hooks/useZoomPan';
import logger from '@/utils/logger';

export interface ZoomComboControlProps {
  currentZoom: ZoomLevel;
  onZoomChange: (level: ZoomLevel) => void;
  className?: string;
}

/**
 * Format zoom level for display
 */
function formatZoom(level: ZoomLevel): string {
  if (level === 'fit') return 'Fit';
  return `${level}%`;
}

/**
 * Parse user input to ZoomLevel
 */
function parseZoomInput(input: string): ZoomLevel | null {
  const trimmed = input.trim().toLowerCase();

  if (trimmed === 'fit') return 'fit';

  // Try to parse as number (with or without % sign)
  const withoutPercent = trimmed.replace('%', '');
  const num = parseFloat(withoutPercent);

  if (!isNaN(num) && num >= 10 && num <= 400) {
    return Math.round(num);
  }

  return null;
}

export function ZoomComboControl({ currentZoom, onZoomChange, className }: ZoomComboControlProps) {
  const [inputValue, setInputValue] = useState(formatZoom(currentZoom));
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when currentZoom changes externally (e.g., trackpad gesture)
  useEffect(() => {
    if (!isEditing) {
      setInputValue(formatZoom(currentZoom));
    }
  }, [currentZoom, isEditing]);

  const handleInputFocus = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsEditing(false);

    // Try to apply the value on blur
    const parsed = parseZoomInput(inputValue);
    logger.debug('ZoomCombo', 'Blur, input:', inputValue, 'parsed:', parsed);
    if (parsed !== null) {
      onZoomChange(parsed);
      setInputValue(formatZoom(parsed));
    } else {
      // Invalid input - restore current zoom
      setInputValue(formatZoom(currentZoom));
    }
  }, [inputValue, currentZoom, onZoomChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const parsed = parseZoomInput(inputValue);
      logger.debug('ZoomCombo', 'Enter pressed, input:', inputValue, 'parsed:', parsed);
      if (parsed !== null) {
        onZoomChange(parsed);
        setInputValue(formatZoom(parsed));
      } else {
        // Invalid input - restore current zoom
        setInputValue(formatZoom(currentZoom));
      }
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      // Cancel edit
      setInputValue(formatZoom(currentZoom));
      inputRef.current?.blur();
    }
  }, [inputValue, currentZoom, onZoomChange]);

  const handlePresetSelect = useCallback((level: ZoomLevel) => {
    onZoomChange(level);
    setInputValue(formatZoom(level));
  }, [onZoomChange]);

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {/* Input for current zoom */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        className={cn(
          'w-20 px-2 py-1 text-sm rounded-l border',
          'border-border bg-background text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          'text-center font-mono'
        )}
        placeholder="Zoom"
        title="Enter zoom level (e.g., '100' or 'fit')"
      />

      {/* Dropdown for presets */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              'px-2 py-1 rounded-r border border-l-0',
              'border-border bg-background text-foreground',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring',
              'flex items-center justify-center'
            )}
            title="Select zoom level"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,4 6,8 10,4" />
            </svg>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={cn(
              'min-w-[120px] bg-popover text-popover-foreground',
              'rounded-md border border-border shadow-lg',
              'p-1 z-[100]',
              'max-h-[300px] overflow-y-auto',
              'pointer-events-auto'
            )}
            sideOffset={4}
            align="end"
          >
            {ZOOM_LEVELS.map((level) => (
              <DropdownMenu.Item
                key={level}
                className={cn(
                  'px-3 py-1.5 text-sm rounded cursor-pointer',
                  'focus:bg-accent focus:text-accent-foreground focus:outline-none',
                  'hover:bg-accent hover:text-accent-foreground',
                  'pointer-events-auto',
                  currentZoom === level && 'bg-accent text-accent-foreground font-semibold'
                )}
                onSelect={() => handlePresetSelect(level)}
              >
                {formatZoom(level)}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
